import { useState } from "react";
import { useGame, AuctionItem } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import AnimBtn from "@/components/base/AnimBtn";
import Confetti from "@/components/base/Confetti";

interface Props {
  auction: AuctionItem;
  onClose: () => void;
}

export default function BidModal({ auction, onClose }: Props) {
  const { placeBid } = useGame();
  const { user, spendCoins } = useAuth();
  const { isDark } = useTheme();
  const [bidAmount, setBidAmount] = useState(auction.currentBid + 50);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [msg, setMsg] = useState("");
  const [showConfetti, setShowConfetti] = useState(false);

  const minBid = auction.currentBid + 1;
  const isValid = bidAmount >= minBid;
  const canAfford = user ? user.coins >= bidAmount : false;

  const handleBid = async () => {
    if (!user) return;
    if (!isValid) {
      setMsg(`最低入札額は ${minBid} coins です`);
      setStatus("error");
      return;
    }
    if (!canAfford) {
      setMsg(`コインが不足しています（残高: ${user.coins} coins）`);
      setStatus("error");
      return;
    }
    const paid = await spendCoins(bidAmount);
    if (!paid) {
      setMsg("コインの消費に失敗しました");
      setStatus("error");
      return;
    }
    placeBid(auction.id, bidAmount, user.id, user.username);
    setMsg(`${bidAmount.toLocaleString()} coins で入札しました！`);
    setStatus("success");
    setShowConfetti(true);
    setTimeout(() => {
      setShowConfetti(false);
      onClose();
    }, 2200);
  };

  const quickAmounts = [
    auction.currentBid + 50,
    auction.currentBid + 100,
    auction.currentBid + 200,
    auction.currentBid + 500,
  ];

  const overlay = isDark ? "bg-[#0f1015] border-white/10" : "bg-white border-gray-200";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";
  const inputBg = isDark ? "bg-white/5 border-white/10 text-white" : "bg-gray-50 border-gray-200 text-gray-900";

  return (
    <>
      <Confetti active={showConfetti} count={100} duration={2000} />
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>
        <div className={`relative w-full max-w-md rounded-2xl border shadow-2xl overflow-hidden animate-slide-up-fade ${overlay}`}>
          {/* Header */}
          <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? "border-white/5" : "border-gray-100"}`}>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 flex items-center justify-center bg-amber-400/10 rounded-lg">
                <i className="ri-auction-line text-amber-400 text-base"></i>
              </div>
              <span className={`font-bold text-sm ${textPrimary}`}>入札する</span>
            </div>
            <AnimBtn
              onClick={onClose}
              animType="pop"
              className={`w-7 h-7 flex items-center justify-center rounded-full cursor-pointer transition-colors ${isDark ? "bg-white/5 hover:bg-white/10 text-gray-400" : "bg-gray-100 hover:bg-gray-200 text-gray-500"}`}
            >
              <i className="ri-close-line text-sm"></i>
            </AnimBtn>
          </div>

          <div className="p-6 space-y-5">
            {/* Auction Info */}
            <div className={`rounded-xl p-4 space-y-2 ${isDark ? "bg-white/3 border border-white/5" : "bg-gray-50 border border-gray-100"}`}>
              <div className="flex justify-between text-xs">
                <span className={textSecondary}>区画座標</span>
                <span className={`font-mono font-semibold ${textPrimary}`}>
                  {auction.blockLat.toFixed(4)}, {auction.blockLng.toFixed(4)}
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className={textSecondary}>出品者</span>
                <span className={`font-semibold ${textPrimary}`}>{auction.sellerName}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className={textSecondary}>現在の最高入札</span>
                <span className="text-amber-400 font-bold text-sm">{auction.currentBid.toLocaleString()} coins</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className={textSecondary}>最高入札者</span>
                <span className={`font-semibold ${auction.currentBidder ? "text-emerald-400" : textSecondary}`}>
                  {auction.currentBidder ?? "なし"}
                </span>
              </div>
              {user && (
                <div className="flex justify-between text-xs">
                  <span className={textSecondary}>あなたの残高</span>
                  <span className={`font-bold ${user.coins >= minBid ? "text-emerald-400" : "text-red-400"}`}>
                    {user.coins.toLocaleString()} coins
                  </span>
                </div>
              )}
            </div>

            {/* Quick amounts */}
            <div>
              <p className={`text-xs font-semibold mb-2 ${textSecondary}`}>クイック入札</p>
              <div className="grid grid-cols-4 gap-2">
                {quickAmounts.map((amt) => (
                  <AnimBtn
                    key={amt}
                    onClick={() => setBidAmount(amt)}
                    animType="pop"
                    className={`text-xs rounded-xl py-2 font-semibold cursor-pointer whitespace-nowrap ${
                      bidAmount === amt
                        ? "bg-amber-400 text-black"
                        : isDark
                        ? "bg-white/5 hover:bg-white/10 text-gray-300"
                        : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                    }`}
                  >
                    {amt}
                  </AnimBtn>
                ))}
              </div>
            </div>

            {/* Custom amount */}
            <div>
              <p className={`text-xs font-semibold mb-2 ${textSecondary}`}>入札額（最低 {minBid} coins）</p>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={bidAmount}
                  min={minBid}
                  onChange={(e) => setBidAmount(Number(e.target.value))}
                  className={`flex-1 rounded-xl border px-4 py-3 text-sm outline-none transition-colors ${inputBg} ${
                    !isValid ? "border-red-400/50" : isDark ? "focus:border-amber-400/50" : "focus:border-amber-400"
                  }`}
                />
                <span className={`flex items-center text-sm font-semibold ${textSecondary}`}>coins</span>
              </div>
              {!isValid && (
                <p className="text-red-400 text-xs mt-1 animate-shake">最低入札額: {minBid} coins</p>
              )}
            </div>

            {/* Status message */}
            {msg && (
              <div className={`rounded-xl px-4 py-3 text-sm flex items-center gap-2 animate-success-burst ${
                status === "success"
                  ? "bg-emerald-400/10 border border-emerald-400/20 text-emerald-400"
                  : "bg-red-400/10 border border-red-400/20 text-red-400"
              }`}>
                <i className={status === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"}></i>
                {msg}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <AnimBtn
                onClick={onClose}
                animType="pop"
                className={`flex-1 rounded-xl py-3 text-sm font-medium cursor-pointer whitespace-nowrap border ${
                  isDark ? "border-white/10 text-gray-400 hover:text-white" : "border-gray-200 text-gray-500 hover:text-gray-900"
                }`}
              >
                キャンセル
              </AnimBtn>
              <AnimBtn
                onClick={handleBid}
                animType="bounce"
                coinEffect
                disabled={!isValid || !canAfford || status === "success"}
                className="flex-1 bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 disabled:opacity-40 text-black font-bold rounded-xl py-3 text-sm cursor-pointer whitespace-nowrap"
              >
                {status === "success" ? (
                  <span className="flex items-center justify-center gap-1">
                    <i className="ri-checkbox-circle-line"></i> 入札完了！
                  </span>
                ) : (
                  `${bidAmount.toLocaleString()} coins で入札`
                )}
              </AnimBtn>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
